import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; 
import { authService } from "../services/authService"; 

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialized = useRef(false);
  
  // Corrected useEffect to fix the Loading screen hang
  useEffect(() => {
    // Prevent multiple initializations
    if (initialized.current) return;

    const initAuth = async () => {
      try {
        setLoading(true); // Start loading state
        const token = localStorage.getItem('token');

        if (token) {
          // Check if user is already in storage to avoid unnecessary API calls
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          } else {
            // No stored user, fetch fresh data
            const profile = await authService.getProfile();
            if (profile && profile.success) {
              const userData = profile.data || profile.user;
              const finalUserData = {
                id: userData.id || userData._id,
                email: userData.email,
                name: userData.name || 'User',
                role: (userData.role || 'student').toLowerCase()
              };
              localStorage.setItem('user', JSON.stringify(finalUserData));
              setUser(finalUserData);
            }
          }
        }
      } catch (error) {
        console.error("Auth initialization failed:", error);
      } finally {
        // CRITICAL FIX: Always stop loading and mark as initialized
        setLoading(false);
        initialized.current = true;
      }
    };

    initAuth();
  }, []); // EMPTY ARRAY is essential to prevent infinite loops

  // LOGIN
  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authService.login({ email, password });
      
      // Extract user data from response
      let userData = null;
      let token = null;
      
      if (response.user && response.token) {
        userData = response.user;
        token = response.token;
      } else if (response.data?.user && response.data?.token) {
        userData = response.data.user;
        token = response.data.token;
      } else if (response.success && response.user && response.token) {
        userData = response.user;
        token = response.token;
      } else if (response.id || response._id) {
        userData = response;
        token = response.token;
      }
      
      if (userData && token) {
        const finalUserData = {
          id: userData.id || userData._id,
          email: userData.email,
          name: userData.name || userData.username || userData.email?.split('@')[0] || 'User',
          role: (userData.role || 'student').toLowerCase()
        };
        setUser(finalUserData);
        navigate("/dashboard", { replace: true });
        return { success: true, user: finalUserData };
      } else {
        console.error("AuthContext: Missing user data or token in response", response);
        throw new Error("Invalid login response structure");
      }
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      setError(error);
      throw error;
    }
  };

  // SIGNUP
  const register = async (formData) => {
    try {
      setError(null);
      const response = await authService.register(formData);
      return response;
    } catch (error) {
      console.error("Registration failed:", error);
      setError(error);
      throw error;
    }
  };

  // LOGOUT
  const logout = () => {
    authService.logout();
    setUser(null);
    setError(null);
    navigate("/login", { replace: true });
  };

  // MANUALLY SET USER (for testing or special cases)
  const setUserManually = (userData) => {
    if (userData) {
      const finalUserData = {
        id: userData.id || userData._id,
        email: userData.email,
        name: userData.name || userData.username || userData.email?.split('@')[0] || 'User',
        role: (userData.role || 'student').toLowerCase()
      };
      localStorage.setItem('user', JSON.stringify(finalUserData));
      setUser(finalUserData);
    }
  };

  // CLEAR ERROR
  const clearError = () => {
    setError(null);
  };

  // REFRESH USER DATA
  const refreshUser = async () => {
    try {
      const profile = await authService.getProfile();
      
      let userData = null;
      if (profile && profile.success && profile.data) {
        userData = profile.data;
      } else if (profile && profile.success && profile.user) {
        userData = profile.user;
      } else if (profile && (profile.id || profile._id)) {
        userData = profile;
      }
      
      if (userData) {
        const finalUserData = {
          id: userData.id || userData._id,
          email: userData.email,
          name: userData.name || userData.username || userData.email?.split('@')[0] || 'User',
          role: (userData.role || 'student').toLowerCase()
        };
        
        localStorage.setItem('user', JSON.stringify(finalUserData));
        setUser(finalUserData);
        return finalUserData;
      }
    } catch (error) {
      console.error("AuthContext: Error refreshing user:", error);
      throw error;
    }
  };

  // UPDATE PROFILE (name/phone/email)
  const updateProfile = async (payload) => {
    const result = await authService.updateProfile(payload);
    // After successful update, refresh lightweight user in context/localStorage
    await refreshUser();
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        register,
        logout,
        loading,
        error,
        clearError,
        setUser: setUserManually,
        refreshUser,
        updateProfile,
        isAuthenticated: authService.isAuthenticated,
        getCurrentUser: authService.getCurrentUser,
        getToken: () => localStorage.getItem('token')
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}